import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getToken } from '../../../../features/token/tokenSaver';
import { getCampaignDetails } from '../../../../services/getCampaignDetails';
import { usePlatformAdvertisingAgreement } from '../../../../hooks/usePlatformAdvertisingAgreement';
import { platformAgreementPath } from '../../../../utils/platformAgreementUrl';
import { PLAYS_PER_SCREEN_MONTH } from '../../../../utils/advertiserScreenPricing';
import PaymentButton from '../AdvertiserDashboardNewCampaign/PaymentButton/PaymentButton';
import '../AdvertiserDashboardNewCampaign/AdvertiserDashboardNewCampaign.css';

function isCampaignEnded(subscriptionEndDate) {
    if (!subscriptionEndDate) return false;
    const end = new Date(subscriptionEndDate);
    if (Number.isNaN(end.getTime())) return false;
    return end.getTime() < Date.now();
}

export default function AdvertiserDashboardRenewCampaign() {
    const { state } = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = useSelector(getToken);

    const adIdFromQuery = parseInt(searchParams.get('adId'), 10);
    const adId = state?.adId || (Number.isFinite(adIdFromQuery) ? adIdFromQuery : null);

    const [adData, setAdData] = useState(null);
    const [targetedScreens, setTargetedScreens] = useState([]);
    const [monthlyFee, setMonthlyFee] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [showPayment, setShowPayment] = useState(false);
    const [pendingRedirect, setPendingRedirect] = useState(false);
    const [tosAccepted, setTosAccepted] = useState(false);
    const [tosError, setTosError] = useState('');

    const isNetworkCampaign = adData?.TargetingMode !== 'selected_screens';
    const agreementOptions = isNetworkCampaign
        ? { screenCount: 0 }
        : {
            screenCount: targetedScreens.length,
            selectedScreenIds: targetedScreens,
        };
    const { doc: agreementDoc } = usePlatformAdvertisingAgreement(
        monthlyFee || Number(adData?.MonthlyFee) || 0,
        agreementOptions
    );
    const tosVersion = agreementDoc?.version;
    const agreementLink = platformAgreementPath(
        monthlyFee || Number(adData?.MonthlyFee) || 0,
        agreementOptions
    );

    useEffect(() => {
        if (searchParams.get('stripe') === 'cancelled') {
            setSubmitError('Checkout was cancelled. You can try again when you are ready.');
        }
    }, [searchParams]);

    useEffect(() => {
        if (!adId) {
            navigate('/advertiserDashboard/campaigns', { replace: true });
            return;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError('');

        getCampaignDetails(token, adId)
            .then((response) => {
                if (cancelled) return;
                const data = response.data?.advertismentData || {};
                const screens = response.data?.targetedScreens || [];
                if (!isCampaignEnded(data.SubscriptionEndDate)) {
                    setLoadError('This campaign has not ended yet, so it cannot be renewed.');
                    setAdData(data);
                    setTargetedScreens(screens);
                    return;
                }
                setAdData(data);
                setTargetedScreens(screens);
                setMonthlyFee(Number(data.MonthlyFee) || 0);
            })
            .catch(() => {
                if (!cancelled) {
                    setLoadError('Unable to load campaign details right now. Please try again.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [adId, token, navigate]);

    function formatDate(dateValue) {
        if (!dateValue) return 'N/A';
        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    }

    function openPayment() {
        setTosAccepted(false);
        setTosError('');
        setShowPayment(true);
    }

    function closePaymentModal() {
        setShowPayment(false);
        setTosAccepted(false);
        setTosError('');
    }

    if (loading) {
        return (
            <div className="advertiserDashboardContainerCampaigns">
                <div className="adminDashboardScreenPageContainer">
                    <div className="campaignDetailsStateCard">Loading renewal details...</div>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="advertiserDashboardContainerCampaigns">
                <div className="adminDashboardScreenPageContainer">
                    <div className="campaignModalAlert" style={{ maxWidth: '640px' }}>
                        <span className="registeredScreensTitle advertiserListTitle">Renew campaign</span>
                        <p style={{ padding: '1rem', textAlign: 'center', color: '#C52F00' }}>{loadError}</p>
                        <button
                            type="button"
                            className="advertiserRegisterSubmitBtn"
                            onClick={() => navigate(adId
                                ? '/advertiserDashboard/campaignDetails'
                                : '/advertiserDashboard/campaigns', {
                                state: adId ? { adId } : undefined,
                            })}
                        >
                            Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const pendingFormData = {
        campaignName: adData?.AdvertisementName,
        monthlyFee,
        selectedScreenIds: targetedScreens,
        advertisementId: adId,
        storefront: adData?.Storefront ? 1 : 0,
    };

    const canProceedToPayment = monthlyFee > 0 && (isNetworkCampaign || targetedScreens.length > 0);

    return (
        <div className="advertiserDashboardContainerCampaigns">
            {submitError && (
                <>
                    <div className="campaignModalAlert">
                        <span className="registeredScreensTitle advertiserListTitle">Notice</span>
                        <p style={{ padding: '1rem', textAlign: 'center', color: '#C52F00' }}>{submitError}</p>
                        <input
                            type="button"
                            onClick={() => setSubmitError('')}
                            className="advertiserRegisterSubmitBtn"
                            value="Close"
                        />
                    </div>
                    <div className="campaignModalBackdrop" />
                </>
            )}

            <div className="adminDashboardScreenPageContainer">
                <span className="registeredScreensTitle advertiserListTitle">Renew campaign</span>

                <div className="monthlyFeeBanner">
                    <span className="monthlyFeeText">{adData?.AdvertisementName || 'Untitled Campaign'}</span>
                    <span className="monthlyFeeSubtext">
                        This campaign ended on {formatDate(adData?.SubscriptionEndDate)}. Renew with a one-time payment
                        to run for another month with the same creatives
                        {isNetworkCampaign ? ' across the full network.' : ' and screens.'}
                    </span>
                    <span className="monthlyFeeNote">
                        {isNetworkCampaign
                            ? `Network (all screens) · $${Number(monthlyFee).toFixed(2)}`
                            : (
                                <>
                                    {targetedScreens.length} screen{targetedScreens.length === 1 ? '' : 's'} ·{' '}
                                    {(targetedScreens.length * PLAYS_PER_SCREEN_MONTH).toLocaleString()} guaranteed plays ·{' '}
                                    ${Number(monthlyFee).toFixed(2)}
                                </>
                            )}
                    </span>
                </div>

                <button
                    type="button"
                    className="advertiserRegisterSubmitBtn"
                    onClick={openPayment}
                    disabled={!canProceedToPayment}
                >
                    Continue to payment
                </button>
                <button
                    type="button"
                    className="advertiserRegisterSubmitBtn"
                    style={{ marginTop: '0.75rem', backgroundColor: '#666' }}
                    onClick={() => navigate('/advertiserDashboard/campaignDetails', { state: { adId } })}
                >
                    Back to campaign
                </button>
            </div>

            {showPayment && (
                <div className="campaignModalAlert" style={{ maxWidth: '800px', width: 'min(92vw, 800px)', maxHeight: '90dvh' }}>
                    <span className="registeredScreensTitle advertiserListTitle">Complete renewal</span>
                    <p style={{ padding: '1rem', textAlign: 'center' }}>
                        One-time payment: ${Number(monthlyFee).toFixed(2)} for another month
                    </p>
                    <p style={{ padding: '0 1rem 1rem', textAlign: 'center', fontSize: '0.9rem' }}>
                        Existing creatives stay in place. After payment, the campaign becomes active again.
                    </p>
                    {pendingRedirect ? (
                        <div className="campaignModalLoadingOverlay">
                            <p style={{ marginBottom: '1rem', color: '#4CAF50' }}>Redirecting to secure checkout…</p>
                        </div>
                    ) : (
                        <div style={{ padding: '1rem' }}>
                            <label className="campaign-tos-checkbox">
                                <input
                                    type="checkbox"
                                    checked={tosAccepted}
                                    onChange={(e) => {
                                        setTosAccepted(e.target.checked);
                                        if (e.target.checked) setTosError('');
                                    }}
                                />
                                <span>
                                    I agree to the{' '}
                                    <Link
                                        to={agreementLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="campaign-tos-link"
                                        onClick={(ev) => ev.stopPropagation()}
                                    >
                                        INVERT Advertising Agreement
                                    </Link>{' '}
                                    {tosVersion ? `(version ${tosVersion})` : ''} for this campaign renewal.
                                </span>
                            </label>
                            {tosError && <p className="campaign-tos-error">{tosError}</p>}
                            <PaymentButton
                                setPendingRedirect={setPendingRedirect}
                                data={pendingFormData}
                                cost={Number(monthlyFee).toFixed(2)}
                                billingMode="one_time_renew"
                                advertisementId={adId}
                                tosAccepted={tosAccepted}
                                onTosError={setTosError}
                                onCheckoutError={(msg) => {
                                    setSubmitError(msg);
                                    closePaymentModal();
                                }}
                            />
                            <button
                                type="button"
                                onClick={closePaymentModal}
                                className="advertiserRegisterSubmitBtn"
                                style={{ marginTop: '1rem', backgroundColor: '#666' }}
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            )}
            {showPayment && <div className="campaignModalBackdrop" />}
        </div>
    );
}
